import React, { useState, useEffect } from 'react';
import { Send, Users, MessageSquare, Plus, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Contact {
  id: string;
  phone_number: string;
  name?: string;
  tags: string[];
  platform: 'whatsapp' | 'facebook' | 'instagram' | 'telegram';
}

interface BroadcastMessage {
  id: string;
  name: string;
  message: string;
  template_id?: string;
  recipient_count: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
  created_at: string;
  sent_at?: string;
  scheduled_at?: string;
  target_tags: string[];
  target_contacts: string[];
  platform: 'whatsapp' | 'facebook' | 'instagram' | 'telegram';
}

interface MessageTemplate {
  id: string;
  name: string;
  content: string;
  variables: string[];
  category: 'marketing' | 'utility' | 'authentication';
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  platform: 'whatsapp' | 'facebook' | 'instagram' | 'telegram';
}

const BroadcastManager: React.FC = () => {
  const [broadcasts, setBroadcasts] = useState<BroadcastMessage[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [showCreateBroadcast, setShowCreateBroadcast] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Mock data
  useEffect(() => {
    const mockContacts: Contact[] = [
      { id: '1', phone_number: '+1234567890', name: 'John Doe', tags: ['customer', 'vip'], platform: 'whatsapp' },
      { id: '2', phone_number: '+1234567891', name: 'Jane Smith', tags: ['prospect'], platform: 'whatsapp' }
    ];

    const mockTemplates: MessageTemplate[] = [
      {
        id: '1',
        name: 'Welcome Message',
        content: 'Hello {{name}}, welcome to our service! We\'re excited to have you on board.',
        variables: ['name'],
        category: 'utility',
        status: 'approved',
        platform: 'whatsapp'
      },
      {
        id: '2',
        name: 'Promotional Offer',
        content: 'Hi {{name}}! Special offer for you: {{discount}}% off on your next purchase. Use code {{code}}.',
        variables: ['name', 'discount', 'code'],
        category: 'marketing',
        status: 'pending',
        platform: 'whatsapp'
      }
    ];

    const mockBroadcasts: BroadcastMessage[] = [
      {
        id: '1',
        name: 'Holiday Promotion',
        message: 'Special holiday offer - 20% off all items!',
        recipient_count: 150,
        sent_count: 150,
        delivered_count: 142,
        read_count: 98,
        status: 'sent',
        created_at: '2024-01-15T10:00:00Z',
        sent_at: '2024-01-15T14:00:00Z',
        target_tags: ['customer'],
        target_contacts: [],
        platform: 'whatsapp'
      }
    ];

    setContacts(mockContacts);
    setTemplates(mockTemplates);
    setBroadcasts(mockBroadcasts);
  }, []);

  const filteredBroadcasts = broadcasts.filter(broadcast => {
    const matchesSearch = broadcast.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         broadcast.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === '' || broadcast.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const CreateBroadcastForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const [formData, setFormData] = useState({
      name: '',
      message: '',
      template_id: '',
      target_type: 'tags', // 'tags' | 'contacts' | 'all'
      target_tags: [] as string[],
      target_contacts: [] as string[],
      platform: 'whatsapp' as const,
      schedule_type: 'now', // 'now' | 'later'
      scheduled_at: ''
    });

    const [availableTags] = useState(['customer', 'vip', 'prospect', 'interested']);


    useEffect(() => {
      if (formData.template_id) {
        const template = templates.find(t => t.id === formData.template_id);
        if (template) {
          setFormData(prev => ({ ...prev, message: template.content }));
        }
      }
    }, [formData.template_id]);

    const getTargetCount = () => {
      if (formData.target_type === 'all') {
        return contacts.length;
      } else if (formData.target_type === 'tags') {
        return contacts.filter(c => c.tags.some(tag => formData.target_tags.includes(tag))).length;
      } else {
        return formData.target_contacts.length;
      }
    };

    const handleSend = () => {
      const newBroadcast: BroadcastMessage = {
        id: Date.now().toString(),
        name: formData.name,
        message: formData.message,
        template_id: formData.template_id || undefined,
        recipient_count: getTargetCount(),
        sent_count: 0,
        delivered_count: 0,
        read_count: 0,
        status: formData.schedule_type === 'now' ? 'sending' : 'scheduled',
        created_at: new Date().toISOString(),
        scheduled_at: formData.scheduled_at || undefined,
        target_tags: formData.target_tags,
        target_contacts: formData.target_contacts,
        platform: formData.platform
      };

      setBroadcasts(prev => [newBroadcast, ...prev]);
      
      if (formData.schedule_type === 'now') {
        toast.success('Broadcast is being sent!');
        // Simulate sending process
        setTimeout(() => {
          setBroadcasts(prev => prev.map(b => 
            b.id === newBroadcast.id 
              ? { ...b, status: 'sent', sent_count: b.recipient_count, sent_at: new Date().toISOString() }
              : b
          ));
        }, 2000);
      } else {
        toast.success('Broadcast scheduled successfully!');
      }
      
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">Create Broadcast Message</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ×
            </button>
          </div>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter campaign name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                <select
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value as any })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="whatsapp">WhatsApp</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="telegram">Telegram</option>
                </select>
              </div>
            </div>

            {/* Template Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message Template (Optional)</label>
              <select
                value={formData.template_id}
                onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Create custom message</option>
                {templates.filter(t => t.platform === formData.platform).map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.status})
                  </option>
                ))}
              </select>
            </div>

            {/* Message Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message Content</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Enter your message content"
              />
              <div className="mt-1 text-sm text-gray-500">
                Character count: {formData.message.length}
              </div>
            </div>

            {/* Target Audience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="target-all"
                    name="target_type"
                    value="all"
                    checked={formData.target_type === 'all'}
                    onChange={(e) => setFormData({ ...formData, target_type: e.target.value as any })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="target-all" className="ml-2 text-sm text-gray-700">
                    All contacts ({contacts.length})
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="target-tags"
                    name="target_type"
                    value="tags"
                    checked={formData.target_type === 'tags'}
                    onChange={(e) => setFormData({ ...formData, target_type: e.target.value as any })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="target-tags" className="ml-2 text-sm text-gray-700">
                    By tags
                  </label>
                </div>

                {formData.target_type === 'tags' && (
                  <div className="ml-6 space-y-2">
                    {availableTags.map(tag => (
                      <div key={tag} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`tag-${tag}`}
                          checked={formData.target_tags.includes(tag)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, target_tags: [...formData.target_tags, tag] });
                            } else {
                              setFormData({ ...formData, target_tags: formData.target_tags.filter(t => t !== tag) });
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor={`tag-${tag}`} className="ml-2 text-sm text-gray-700 capitalize">
                          {tag} ({contacts.filter(c => c.tags.includes(tag)).length})
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Scheduling */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Schedule</label>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="send-now"
                    name="schedule_type"
                    value="now"
                    checked={formData.schedule_type === 'now'}
                    onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value as any })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="send-now" className="ml-2 text-sm text-gray-700">
                    Send now
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="send-later"
                    name="schedule_type"
                    value="later"
                    checked={formData.schedule_type === 'later'}
                    onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value as any })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="send-later" className="ml-2 text-sm text-gray-700">
                    Schedule for later
                  </label>
                </div>

                {formData.schedule_type === 'later' && (
                  <div className="ml-6">
                    <input
                      type="datetime-local"
                      value={formData.scheduled_at}
                      onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                      className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Target Count Display */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-blue-800 font-medium">
                  This message will be sent to {getTargetCount()} contacts
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-8">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={!formData.name || !formData.message || getTargetCount() === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 flex items-center"
            >
              <Send size={16} className="mr-1" />
              {formData.schedule_type === 'now' ? 'Send Now' : 'Schedule'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'text-green-600 bg-green-100';
      case 'sending': return 'text-blue-600 bg-blue-100';
      case 'scheduled': return 'text-yellow-600 bg-yellow-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="p-6 bg-white">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Broadcast Manager</h2>
          <p className="text-gray-600">Create and manage broadcast messages to your contacts</p>
        </div>
        <button
          onClick={() => setShowCreateBroadcast(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
        >
          <Plus size={16} className="mr-1" />
          Create Broadcast
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search broadcasts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="sending">Sending</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Broadcasts List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredBroadcasts.map((broadcast) => (
            <li key={broadcast.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{broadcast.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{broadcast.message}</p>
                      <div className="flex items-center mt-2 space-x-4 text-sm text-gray-500">
                        <span>Platform: {broadcast.platform}</span>
                        <span>Recipients: {broadcast.recipient_count}</span>
                        {broadcast.sent_at && (
                          <span>Sent: {new Date(broadcast.sent_at).toLocaleDateString()}</span>
                        )}
                        {broadcast.scheduled_at && (
                          <span>Scheduled: {new Date(broadcast.scheduled_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(broadcast.status)}`}>
                        {broadcast.status}
                      </span>
                      
                      {broadcast.status === 'sent' && (
                        <div className="text-sm text-gray-600">
                          <div>Sent: {broadcast.sent_count}</div>
                          <div>Delivered: {broadcast.delivered_count}</div>
                          <div>Read: {broadcast.read_count}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
        
        {filteredBroadcasts.length === 0 && (
          <div className="text-center py-8">
            <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No broadcasts found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first broadcast message
            </p>
          </div>
        )}
      </div>

      {/* Create Broadcast Modal */}
      {showCreateBroadcast && (
        <CreateBroadcastForm onClose={() => setShowCreateBroadcast(false)} />
      )}
    </div>
  );
};

export default BroadcastManager; 